interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
}
declare global {
  interface Window {
    SpeechRecognition?: { new (): SpeechRecognitionLike };
    webkitSpeechRecognition?: { new (): SpeechRecognitionLike };
  }
}

import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  ChatConversation,
  ChatInput,
  ChatMessage,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetTitle,
  Switch,
  Textarea,
} from '@avila-roffe/components-web';
import {
  ActivityIcon,
  Archive,
  AudioWaveform,
  ChevronDown,
  Clock,
  Eraser,
  Expand,
  Mic,
  MoreVertical,
  Paperclip,
  PenLine,
  SlidersHorizontal,
  SlidersVertical,
  Trash2,
  WifiOff,
  X,
} from 'lucide-react';
import * as React from 'react';
import { AGENT_IDS, AGENT_META } from './agentMeta';
import { useChatStore } from './chatStore';
import type { ChatAttachment } from './types';
import { useSendMessage } from './useSendMessage';

const MODEL_META: Record<string, { label: string; sublabel: string }> = {
  'claude-sonnet-4-6': { label: 'Sonnet 4.6', sublabel: 'Balanced' },
  'claude-opus-4-6': { label: 'Opus 4.6', sublabel: 'Reasoning' },
  'claude-haiku-4-5': { label: 'Haiku 4.5', sublabel: 'Fast' },
  'claude-haiku-4-5-20251001': { label: 'Haiku 4.5', sublabel: 'Fast' },
  'gpt-oss': { label: 'GPT OSS', sublabel: 'Offline' },
  'deepseek-r1': { label: 'DeepSeek R1', sublabel: 'Reasoning' },
};

function modelLabel(id: string): string {
  return MODEL_META[id]?.label ?? id;
}
function modelSublabel(id: string): string {
  return MODEL_META[id]?.sublabel ?? '';
}

const TEXT_EXTENSIONS = new Set([
  '.txt',
  '.md',
  '.csv',
  '.json',
  '.ts',
  '.tsx',
  '.js',
  '.jsx',
  '.py',
  '.sh',
  '.yaml',
  '.yml',
  '.toml',
  '.xml',
  '.html',
  '.css',
]);

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
}

function isTextFile(file: File): boolean {
  if (file.type.startsWith('text/')) return true;
  if (['application/json', 'application/javascript'].includes(file.type)) return true;
  const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  return TEXT_EXTENSIONS.has(ext);
}

function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve((e.target?.result as string) ?? '');
    reader.onerror = reject;
    reader.readAsText(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

const MODEL_NONE = '__default__';

function ConfigPanel() {
  const {
    useAgent,
    setUseAgent,
    selectedAgentId,
    selectedModelId,
    setSelectedModel,
    models,
    systemPrompt,
    setSystemPrompt,
  } = useChatStore();

  const [promptDialogOpen, setPromptDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (useAgent && selectedAgentId) {
      const agentPrompt = AGENT_META[selectedAgentId]?.systemPrompt;
      if (agentPrompt) setSystemPrompt(agentPrompt);
    }
  }, [selectedAgentId, useAgent, setSystemPrompt]);

  const selectValue = selectedModelId || MODEL_NONE;

  function handleModelChange(value: string) {
    setSelectedModel(value === MODEL_NONE ? '' : value);
  }

  return (
    <div className="flex flex-col gap-6 p-4">
      <section>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Select Agent</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {useAgent ? 'Agent handles the conversation' : 'Direct LLM with system prompt'}
            </p>
          </div>
          <Switch
            size="sm"
            checked={useAgent}
            onCheckedChange={setUseAgent}
            aria-label="Use agent"
          />
        </div>
      </section>

      <section>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Model override
        </p>
        <Select value={selectValue} onValueChange={handleModelChange}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue placeholder="Default (agent config)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={MODEL_NONE}>Default (agent config)</SelectItem>
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                <span>{m.label || m.id}</span>
                {m.sublabel && (
                  <span className="ml-1.5 text-muted-foreground text-xs">{m.sublabel}</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedModelId && (
          <button
            type="button"
            className="mt-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
            onClick={() => setSelectedModel('')}
          >
            Reset to default
          </button>
        )}
      </section>

      <section className="flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            System Prompt
          </p>
          <button
            type="button"
            aria-label="Expand system prompt"
            onClick={() => setPromptDialogOpen(true)}
            className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
          >
            <Expand className="size-3.5" />
          </button>
        </div>

        <Textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder={
            useAgent ? "Override the agent's system prompt…" : 'Enter a system prompt for the LLM…'
          }
          rows={6}
          className="bg-background text-base sm:text-xs"
          resize="none"
        />

        {!useAgent && systemPrompt && (
          <button
            type="button"
            className="self-end mt-1 text-xs text-muted-foreground hover:text-destructive transition-colors"
            onClick={() => setSystemPrompt('')}
          >
            Clear
          </button>
        )}
      </section>

      <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
        <DialogContent className="max-w-2xl w-full">
          <DialogHeader>
            <DialogTitle>System Prompt</DialogTitle>
          </DialogHeader>
          <Textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder={
              useAgent
                ? "Override the agent's system prompt…"
                : 'Enter a system prompt for the LLM…'
            }
            rows={20}
            className="bg-background"
            resize="none"
          />
          {!useAgent && systemPrompt && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground hover:text-destructive transition-colors"
              onClick={() => setSystemPrompt('')}
            >
              Clear
            </button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SidePanelContent() {
  const [tab, setTab] = React.useState<'history' | 'config'>('config');

  return (
    <div className="flex flex-col h-full">
      <div className="flex gap-1 p-2 border-b shrink-0">
        <button
          type="button"
          onClick={() => setTab('history')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
            tab === 'history'
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Clock className="size-3.5" />
          History
        </button>
        <button
          type="button"
          onClick={() => setTab('config')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
            tab === 'config'
              ? 'bg-accent text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <SlidersVertical className="size-3.5" />
          Config
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {tab === 'history' ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-4">
            <div className="size-8 rounded-full bg-muted flex items-center justify-center">
              <Clock className="size-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">Previous conversations coming soon</p>
          </div>
        ) : (
          <ConfigPanel />
        )}
      </div>
    </div>
  );
}

export interface ChatContentProps {
  factoryUrl?: string;
  adminToken?: string;
  showSettings?: boolean;
  /** When true, omits the inner agent-selector / actions header row */
  hideHeader?: boolean;
  className?: string;
}

export function ChatContent({
  factoryUrl,
  adminToken,
  showSettings = true,
  hideHeader = false,
  className,
}: ChatContentProps) {
  const {
    messages,
    isProcessing,
    streamingContent,
    reasoningEvents,
    clearSession,
    selectedAgentId,
    setSelectedAgent,
    selectedModelId,
    setSelectedModel,
    useAgent,
    models,
    setModels,
    sessionId,
  } = useChatStore();
  const isConnected = useChatStore((s) => s.isConnected);
  const connectionError = useChatStore((s) => s.connectionError);
  const { sendMessage } = useSendMessage();

  React.useEffect(() => {
    const baseUrl =
      factoryUrl ?? (import.meta.env.VITE_ARAF_FACTORY_URL as string | undefined) ?? '';
    const token = adminToken ?? (import.meta.env.VITE_ARAF_ADMIN_TOKEN as string | undefined) ?? '';
    if (!baseUrl) return;
    fetch(`${baseUrl}/api/v1/chat/models`, {
      headers: { 'x-admin-token': token },
      signal: AbortSignal.timeout(5000),
    })
      .then((res) => (res.ok ? (res.json() as Promise<{ models: Array<{ id: string }> }>) : null))
      .then((body) => {
        if (body?.models && body.models.length > 0) {
          setModels(
            body.models.map((m) => ({
              id: m.id,
              label: modelLabel(m.id),
              sublabel: modelSublabel(m.id),
            })),
          );
        }
      })
      .catch(() => {});
  }, [factoryUrl, adminToken, setModels]);

  const DIRECT_LLM_META = {
    initials: 'LM',
    color: 'bg-slate-100 text-slate-600',
    description: 'Direct LLM · No agent wrapper',
  };
  const agent = selectedAgentId
    ? (AGENT_META[selectedAgentId] ?? {
        initials: selectedAgentId.slice(0, 2).toUpperCase(),
        color: 'bg-slate-100 text-slate-600',
        description: selectedAgentId,
        systemPrompt: '',
      })
    : DIRECT_LLM_META;
  const agentDisplayName = selectedAgentId || 'Direct LLM';
  const activeModel = selectedModelId
    ? (models.find((m) => m.id === selectedModelId) ?? {
        id: selectedModelId,
        label: selectedModelId,
        sublabel: '',
      })
    : models[0];

  const [settingsOpen, setSettingsOpen] = React.useState(false);

  const [inputValue, setInputValue] = React.useState('');
  const [attachedFiles, setAttachedFiles] = React.useState<(ChatAttachment & { id: string })[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [isRecording, setIsRecording] = React.useState(false);
  const recognitionRef = React.useRef<SpeechRecognitionLike | null>(null);
  const voiceBaseRef = React.useRef('');

  const SR =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition ?? window.webkitSpeechRecognition)
      : undefined;
  const voiceSupported = !!SR;

  const startRecording = () => {
    if (!SR) return;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en';

    recognition.onresult = (e) => {
      let transcript = '';
      for (let i = 0; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript;
      }
      setInputValue(voiceBaseRef.current + transcript);
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognition.onerror = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    voiceBaseRef.current = inputValue;
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    recognitionRef.current?.stop();
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const handleFiles = async (rawFiles: File[]) => {
    for (const file of rawFiles) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const base: ChatAttachment & { id: string } = { id, name: file.name, size: file.size };
      if (isTextFile(file)) {
        const text = await readAsText(file).catch(() => undefined);
        setAttachedFiles((prev) => [...prev, { ...base, text }]);
      } else {
        setAttachedFiles((prev) => [...prev, base]);
      }
    }
  };

  const removeFile = (id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async (text: string) => {
    if (isRecording) stopRecording();
    await sendMessage({ text, files: attachedFiles });
    setInputValue('');
    setAttachedFiles([]);
    voiceBaseRef.current = '';
  };

  const leading = (
    <div className="flex items-center gap-0.5">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="sr-only"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) handleFiles(files);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
      />

      {/* Attachment */}
      <Button
        type="button"
        appearance="ghost"
        size="icon"
        className="size-7 rounded-full text-muted-foreground hover:text-foreground"
        aria-label="Attach files"
        onClick={() => fileInputRef.current?.click()}
      >
        <Paperclip className="size-4" />
      </Button>

      {/* Preferences popover + always-visible model label */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            appearance="ghost"
            size="icon"
            className="size-7 rounded-full text-muted-foreground hover:text-foreground"
            aria-label="Preferences"
          >
            <SlidersHorizontal className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Preferences
          </p>
          {(models.length > 0 || selectedModelId) && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-foreground">Model</p>
              <Select value={selectedModelId || models[0]?.id} onValueChange={setSelectedModel}>
                <SelectTrigger size="sm">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {selectedModelId && !models.some((m) => m.id === selectedModelId) && (
                    <SelectItem key={selectedModelId} value={selectedModelId} className="text-xs">
                      <span className="font-medium">{selectedModelId}</span>
                    </SelectItem>
                  )}
                  {models.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="text-xs">
                      <span className="font-medium">{m.label}</span>
                      {m.sublabel && (
                        <span className="ml-1.5 text-muted-foreground">{m.sublabel}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {activeModel && (
        <span className="text-xs text-muted-foreground pl-0.5 select-none">
          {activeModel.label}
        </span>
      )}
    </div>
  );

  const trailing = voiceSupported ? (
    <div className="flex items-center gap-0.5">
      <Button
        type="button"
        appearance="ghost"
        size="icon"
        aria-label={isRecording ? 'Stop recording' : 'Record voice message'}
        className={`size-7 rounded-full transition-colors ${
          isRecording
            ? 'text-destructive hover:text-destructive animate-pulse'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        onClick={toggleRecording}
      >
        <Mic className="size-4" />
      </Button>
      <Button
        type="button"
        appearance="ghost"
        size="icon"
        aria-label="Audio waveform"
        className={`size-7 rounded-full transition-colors ${
          isRecording
            ? 'text-destructive animate-pulse'
            : 'text-muted-foreground hover:text-foreground'
        }`}
        onClick={toggleRecording}
      >
        <AudioWaveform className="size-4" />
      </Button>
    </div>
  ) : null;

  return (
    <div
      className={['flex flex-col h-full', className].filter(Boolean).join(' ')}
      data-view="ChatContent"
    >
      <h1 className="sr-only">Chat</h1>

      {!hideHeader && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 border-b shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {useAgent ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    appearance="ghost"
                    size="sm"
                    className="flex items-center gap-2 h-8 px-2 hover:bg-accent"
                  >
                    <Avatar size="xs">
                      <AvatarFallback className={agent.color}>{agent.initials}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium capitalize">{agentDisplayName}</span>
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      · {agent.description.split('·')[0].trim()}
                    </span>
                    <ChevronDown className="size-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  {[
                    ...AGENT_IDS,
                    ...(selectedAgentId &&
                    !AGENT_IDS.includes(selectedAgentId as (typeof AGENT_IDS)[number])
                      ? [selectedAgentId]
                      : []),
                  ].map((id) => {
                    const meta = AGENT_META[id] ?? {
                      initials: id.slice(0, 2).toUpperCase(),
                      color: 'bg-slate-100 text-slate-600',
                      description: id,
                    };
                    return (
                      <DropdownMenuItem
                        key={id}
                        className="flex items-center gap-3 py-2"
                        onClick={() => setSelectedAgent(id)}
                      >
                        <Avatar size="xs">
                          <AvatarFallback className={meta.color}>{meta.initials}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium capitalize leading-none">{id}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                        </div>
                        {id === selectedAgentId && (
                          <span className="ml-auto size-1.5 rounded-full bg-primary shrink-0" />
                        )}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <p className="text-sm text-muted-foreground px-2">Direct LLM</p>
            )}
            {sessionId && (
              <span
                title={sessionId}
                className="font-mono text-[10px] text-muted-foreground/50 hidden sm:block truncate max-w-[120px]"
              >
                {sessionId.slice(0, 8)}
              </span>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                appearance="ghost"
                size="icon"
                className="size-8 text-muted-foreground"
                aria-label="Chat actions"
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={clearSession}>
                <PenLine className="size-4 mr-2 shrink-0" />
                New Conversation
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Archive className="size-4 mr-2 shrink-0" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuItem onClick={clearSession}>
                <Eraser className="size-4 mr-2 shrink-0" />
                Clear Messages
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive focus:text-destructive" disabled>
                <Trash2 className="size-4 mr-2 shrink-0" />
                Delete
              </DropdownMenuItem>
              {showSettings && (
                <>
                  <DropdownMenuSeparator className="lg:hidden" />
                  <DropdownMenuItem className="lg:hidden" onClick={() => setSettingsOpen(true)}>
                    <SlidersHorizontal className="size-4 mr-2 shrink-0" />
                    Settings
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {!isConnected && (
        <div className="flex items-center gap-2 px-4 py-1.5 text-xs bg-amber-50 text-amber-700 border-b border-amber-200 shrink-0">
          <WifiOff className="size-3.5 shrink-0" />
          <span>{connectionError ?? 'Connecting to agent…'}</span>
        </div>
      )}

      <ChatConversation>
        {messages.length === 0 && !isProcessing && streamingContent.length === 0 && (
          <div className="flex flex-col flex-1 items-center justify-center gap-3 py-12 text-center">
            <Avatar size="lg">
              <AvatarFallback className={agent.color}>{agent.initials}</AvatarFallback>
            </Avatar>
            <div>
              <p className="text-base font-semibold capitalize">{agentDisplayName}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{agent.description}</p>
            </div>
            <p className="text-sm text-muted-foreground">Start a conversation…</p>
          </div>
        )}
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            role={msg.status === 'error' ? 'system' : msg.role}
            content={msg.status === 'error' ? `⚠ ${msg.content}` : msg.content}
            timestamp={formatTimestamp(msg.timestamp)}
            avatar={
              msg.role === 'assistant' ? (
                <Avatar size="xs">
                  <AvatarFallback className={agent.color}>{agent.initials}</AvatarFallback>
                </Avatar>
              ) : undefined
            }
          />
        ))}

        {isProcessing && reasoningEvents.length > 0 && (
          <div className="mx-4 my-1 rounded-lg border border-muted bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-0.5">
            {reasoningEvents.slice(-5).map((ev) => (
              <div key={String(ev.timestamp)} className="flex items-center gap-1.5 truncate">
                <ActivityIcon className="size-3 shrink-0" />
                <span className="truncate">{ev.label}</span>
              </div>
            ))}
          </div>
        )}

        {isProcessing && streamingContent.length > 0 && (
          // biome-ignore lint/a11y/useValidAriaRole: role is a ChatMessage domain prop, not an ARIA role
          <ChatMessage
            role="assistant"
            content={streamingContent}
            timestamp={formatTimestamp(new Date().toISOString())}
            avatar={
              <Avatar size="xs">
                <AvatarFallback className={agent.color}>{agent.initials}</AvatarFallback>
              </Avatar>
            }
          />
        )}

        {isProcessing && streamingContent.length === 0 && (
          // biome-ignore lint/a11y/useValidAriaRole: role is a ChatMessage domain prop, not an ARIA role
          <ChatMessage
            role="assistant"
            content=""
            loading
            avatar={
              <Avatar size="xs">
                <AvatarFallback className={agent.color}>{agent.initials}</AvatarFallback>
              </Avatar>
            }
          />
        )}
      </ChatConversation>

      <div className="shrink-0 px-4 py-3 space-y-2">
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachedFiles.map((f) => (
              <Badge
                key={f.id}
                variant="secondary"
                className="group flex items-center gap-1 h-6 max-w-[12rem] px-2 text-xs font-normal"
              >
                <span className="truncate">{f.name}</span>
                <span className="text-muted-foreground ml-0.5">{formatBytes(f.size)}</span>
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => removeFile(f.id)}
                  className="ml-0.5 text-muted-foreground hover:text-foreground shrink-0"
                >
                  <X className="size-2.5" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {isRecording && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
            <AudioWaveform className="size-3.5 text-destructive animate-pulse" />
            <span>Listening… speak now — transcription will appear in the input</span>
          </div>
        )}

        <ChatInput
          value={inputValue}
          onValueChange={setInputValue}
          onSubmit={handleSubmit}
          disabled={isProcessing}
          placeholder={useAgent ? `Ask ${agentDisplayName} anything…` : 'Ask anything…'}
          leading={leading}
          trailing={trailing}
        />
      </div>

      {showSettings && (
        <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
          <SheetContent side="right" className="w-[280px] p-0 flex flex-col">
            <SheetTitle className="sr-only">Chat Settings</SheetTitle>
            <div className="flex-1 min-h-0 overflow-hidden pt-8">
              <SidePanelContent />
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
